/**
 * The extra Google Ads metrics kept per day for a campaign, an ad group, a keyword and a search term
 * (the `extra` column of m_campaign_daily / m_ad_group_daily / m_keyword_daily /
 * m_search_term_daily), beyond impressions / clicks /
 * cost / conversions / value. One list drives the sync (which fields to ask Google for), the read
 * model (how a date range is added up) and the web columns. Google does not report every metric at
 * every level: `not` lists the levels a metric is missing from.
 *
 * kind:
 * - int / num: counts, summed over days
 * - money: a currency amount from Google, stored and summed in micros
 * - micros: already micros (averaged by cost)
 * - share: a 0..1 ratio Google computes per day; over several days it is weighted by impressions
 *   (search_is exactly: from the eligible impressions it implies)
 * - by_cost: a ratio averaged by cost, as Google averages targets
 */
export type CampaignExtraKind = "int" | "num" | "money" | "micros" | "share" | "by_cost";

/** Where metrics are kept: a campaign, an ad group, a keyword, a search term. */
export type MetricLevel = "campaign" | "ad_group" | "keyword" | "search_term";

export interface CampaignExtraMetric {
  key: string;
  /** the Google Ads metric, as GAQL writes it (after `metrics.`) */
  field: string;
  kind: CampaignExtraKind;
  /** levels Google does not report it at */
  not?: MetricLevel[];
}

const SUB: MetricLevel[] = ["ad_group", "keyword"];
const KW: MetricLevel[] = ["keyword"];

export const CAMPAIGN_EXTRA: CampaignExtraMetric[] = [
  { key: "interactions", field: "interactions", kind: "int" },
  { key: "engagements", field: "engagements", kind: "int" },
  { key: "invalid_clicks", field: "invalid_clicks", kind: "int", not: SUB },
  { key: "general_invalid_clicks", field: "general_invalid_clicks", kind: "int", not: SUB },
  { key: "trueview_views", field: "video_trueview_views", kind: "int" },
  { key: "view_through_conv", field: "view_through_conversions", kind: "int" },
  { key: "phone_calls", field: "phone_calls", kind: "int" },
  { key: "phone_impr", field: "phone_impressions", kind: "int", not: KW },

  { key: "all_conv", field: "all_conversions", kind: "num" },
  { key: "conv_by_time", field: "conversions_by_conversion_date", kind: "num" },
  { key: "all_conv_by_time", field: "all_conversions_by_conversion_date", kind: "num" },
  { key: "cm_conv", field: "current_model_attributed_conversions", kind: "num" },
  { key: "cross_device_conv", field: "cross_device_conversions", kind: "num" },
  { key: "pc_conv", field: "platform_comparable_conversions", kind: "num", not: KW },
  {
    key: "pc_conv_by_time",
    field: "platform_comparable_conversions_by_conversion_date",
    kind: "num",
    not: KW,
  },

  { key: "all_conv_value", field: "all_conversions_value", kind: "money" },
  { key: "conv_value_by_time", field: "conversions_value_by_conversion_date", kind: "money" },
  { key: "all_conv_value_by_time", field: "all_conversions_value_by_conversion_date", kind: "money" },
  { key: "cm_conv_value", field: "current_model_attributed_conversions_value", kind: "money" },
  { key: "new_customer_ltv", field: "new_customer_lifetime_value", kind: "money", not: KW },
  { key: "all_new_customer_ltv", field: "all_new_customer_lifetime_value", kind: "money", not: KW },
  { key: "pc_conv_value", field: "platform_comparable_conversions_value", kind: "money", not: KW },
  {
    key: "pc_conv_value_by_time",
    field: "platform_comparable_conversions_value_by_conversion_date",
    kind: "money",
    not: KW,
  },

  // a campaign keeps this one in its own column (lost_is_rank); below it, here
  { key: "lost_is_rank", field: "search_rank_lost_impression_share", kind: "share", not: ["campaign"] },
  { key: "search_is", field: "search_impression_share", kind: "share" },
  { key: "search_top_is", field: "search_top_impression_share", kind: "share" },
  { key: "search_abs_top_is", field: "search_absolute_top_impression_share", kind: "share" },
  { key: "lost_top_is_rank", field: "search_rank_lost_top_impression_share", kind: "share" },
  { key: "lost_abs_top_is_rank", field: "search_rank_lost_absolute_top_impression_share", kind: "share" },
  { key: "lost_top_is_budget", field: "search_budget_lost_top_impression_share", kind: "share" },
  { key: "lost_abs_top_is_budget", field: "search_budget_lost_absolute_top_impression_share", kind: "share" },
  { key: "search_exact_is", field: "search_exact_match_impression_share", kind: "share" },
  { key: "click_share", field: "search_click_share", kind: "share", not: ["ad_group"] },
  { key: "abs_top_impr_pct", field: "absolute_top_impression_percentage", kind: "share" },
  { key: "top_impr_pct", field: "top_impression_percentage", kind: "share" },

  { key: "avg_target_roas", field: "average_target_roas", kind: "by_cost", not: SUB },
  { key: "avg_target_cpa", field: "average_target_cpa_micros", kind: "micros", not: SUB },
];

/** Keys whose range value is an amount in micros (returned as strings, like all money). */
export const CAMPAIGN_EXTRA_MONEY = new Set(
  CAMPAIGN_EXTRA.filter((m) => m.kind === "money" || m.kind === "micros").map((m) => m.key),
);

/**
 * The few of them search_term_view has (asked of the API: every other one is refused there with
 * "could not support requested resources: SEARCH_TERM_VIEW").
 */
const SEARCH_TERM_EXTRA = new Set([
  "interactions",
  "engagements",
  "trueview_views",
  "view_through_conv",
  "all_conv",
  "cross_device_conv",
  "all_conv_value",
  "abs_top_impr_pct",
  "top_impr_pct",
]);

/** The metrics Google reports at a level. */
export const extraMetricsFor = (level: MetricLevel): CampaignExtraMetric[] =>
  level === "search_term"
    ? CAMPAIGN_EXTRA.filter((m) => SEARCH_TERM_EXTRA.has(m.key))
    : CAMPAIGN_EXTRA.filter((m) => !m.not?.includes(level));
